import React, { useEffect, useState, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Form } from "react-bootstrap";

import {
  PatientContext,
  UserContext,
} from "../../lib/main-context";
import customizeMessages from "../../lib/customizeMessages";
import { useTranslation } from "../../i18n/TranslationProvider";

import format from "date-fns/format";

import { getPatientList3 } from "../../api/auth";

import OnboardingStepHeader from "./OnboardingStepHeader";
import Sallie from "../UIComponents/Sallie";
import CategorySelector from "../UIComponents/CategorySelector";
import SubmitButton from "../UIComponents/SubmitButton";

import brainA from "../../images/brain-a.jpg";
import brainB from "../../images/brain-b.jpg";

import {
  getTBISymptoms,
  postRegisterFactors,
  postNewResultingFactors,
  getTbiDate,
  getStrokeDate,
  getNonTbiDate,
  addSymptomDetails,
} from "../../api/TBIRequests";

const SelectLaterSymptoms = ({ setOnboardingPercent }) => {
  const history = useHistory();
  const location = useLocation();

  const { user } = useContext(UserContext);
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [errorMessage, setErrorMessage] = useState(false);

  const [returnedData, setReturnedData] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [dateOfTBI, setDateOfTBI] = useState(undefined);
  const [headacheParts, setHeadacheParts] = useState([]);

  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);

  // patient_info.patient_type as stored in the DB.
  // - Patient users: `patient` comes from GET /patient/get-patient, which
  //   already returns patientType.
  // - Caregivers: `patient` comes from the POST /patient/register response,
  //   which does NOT return patientType, so look it up from
  //   GET /patient/get-patient-list3 (the user's patients + patient_type).
  const [dbPatientType, setDbPatientType] = useState(
    patient?.patientType ?? null
  );

  useEffect(() => {
    if (!patient?.patientId) return;
    if (patient.patientType) {
      setDbPatientType(patient.patientType);
      return;
    }
    let isMounted = true;
    getPatientList3()
      .then(({ data }) => {
        const match = (data || []).find(
          (p) => p.patientId === patient.patientId
        );
        if (isMounted) setDbPatientType(match?.patientType ?? null);
      })
      .catch((err) => {
        // Non-blocking: fall back to the default (TBI) text.
        console.error("getPatientList3 (patientType lookup) error:", err);
      });
    return () => {
      isMounted = false;
    };
  }, [patient?.patientId, patient?.patientType]);

  useEffect(() => {
    if (location?.state?.date === undefined) {
      if (patient.patientType === "Stroke") {
        getStrokeDate(patient.patientId)
          .then((res) => {
            let TbiDate = format(new Date(res.data.stroke_incident_date), "MM-dd-yyyy");
            setDateOfTBI(TbiDate);
          })
          .catch(() => history.push("/oops"));
      } else if (
        patient.patientType !== "TBI/Concussion" &&
        patient.patientType !== "hasSymptom" &&
        patient.patientType !== "Post-Concussive Syndrome" &&
        patient.patientType !== "tbiPatient"
      ) {
        getNonTbiDate(patient.patientId)
          .then((res) => {
            let symptomDate = format(new Date(res.data.symptoms_date), "MM-dd-yyyy");
            setDateOfTBI(symptomDate);
          })
          .catch((error) => {
            console.error("Error fetching non-TBI date:", error);
            history.push("/oops");
          });
      } else {
        getTbiDate(patient.patientId)
          .then((res) => {
            let TbiDate = format(new Date(res.data.tbi_incident_date), "MM-dd-yyyy");
            setDateOfTBI(TbiDate);
          })
          .catch(() => history.push("/oops"));
      }
    } else {
      setDateOfTBI(format(new Date(location?.state?.date), "MM-dd-yyyy"));
    }
  }, []);

  useEffect(() => {
    location.state?.fromPatientControl
      ? setOnboardingPercent(60)
      : setOnboardingPercent(40);

    const fetchSymptoms = async () => {
      try {
        const res = await getTBISymptoms();
        const processedList = categories.map((category) => {
          const items = res.data
            .filter((item) => item.subcategory === category)
            .map((item) => item.factor);
          return { category, items };
        });
        setReturnedData(processedList);
        setLoading(false);
      } catch (err) {
        console.error("fetchSymptoms error:", err);
        history.push("/oops");
      }
    };

    fetchSymptoms();
  }, []);

  const getPossibleMessages = () => ({
    mainHeader: {
      Stroke: t("selectlatersymptoms.header"),
      tbiPatient: t("selectlatersymptoms.header"),
      caregiver: t("selectlatersymptoms.header"),
    },
    sallieText: {
      Stroke: t("selectlatersymptoms.sallie.stroke"),
      tbiPatient: t("selectlatersymptoms.sallie.tbi"),
      caregiver: t("selectlatersymptoms.sallie.caregiver"),
    },
    noSymptomsText: {
      Stroke: t("selectlatersymptoms.error.no_symptom.patient"),
      tbiPatient: t("selectlatersymptoms.error.no_symptom.patient"),
      caregiver: t("selectlatersymptoms.error.no_symptom.caregiver"),
    },
    selectHeadacheLocationText: {
      Stroke: t("selectlatersymptoms.error.headache_location"),
      tbiPatient: t("selectlatersymptoms.error.headache_location"),
      caregiver: t("selectlatersymptoms.error.headache_location"),
    },
  });

  const getBaseMessage = customizeMessages({ user, getPossibleMessages });

  // SMI patients/caregivers: Sallie says "brain condition" instead of "TBI".
  // Everyone else keeps the existing text from customizeMessages.
  // "SMI" is the patientType BasicInfo/PatientInfo send to
  // POST /patient/register, stored as patient_info.patient_type.
  const isSMI = dbPatientType === "SMI";
  const isCaregiver = user?.userType === "caregiver";

  const getMessage = (key) => {
    if (key === "sallieText" && isSMI) {
      return isCaregiver
        ? t("selectlatersymptoms.sallie.smi.caregiver")
        : t("selectlatersymptoms.sallie.smi.patient");
    }
    return getBaseMessage(key);
  };

  const categories = ["cognitive", "sleep", "emotional", "physical", "speech", "vision"];

  const colors = {
    cognitive: "#91a5eb",
    sleep: "#f5b9bb",
    physical: "#f5c389",
    emotional: "#fbec5d",
    speech: "#a7d69f",
    vision: "#9be0da",
  };

  const handlePrev = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex((prev) => prev - 1);
    }
  };

  const handleNextOrSubmit = () => {
    if (currentCategoryIndex < categories.length - 1) {
      setCurrentCategoryIndex((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const processDataForSending = () => {
    return Object.entries(selectedItems).flatMap(([category, itemList]) => {
      return itemList.map((item) => ({
        factor: item,
        category: "medical",
        subcategory: category,
        datetimeOfSymptom: dateOfTBI,
        hadSymptom: true,
      }));
    });
  };

  const handleSubmit = () => {
    setSubmitting(true);
    let processedData = processDataForSending();

    if (processedData.length < 3) {
      setErrorMessage(true);
      setSubmitting(false);
      return;
    }

    try {
      if (processedData.length === 0) {
        setError(t("selectlatersymptoms.error.no_symptom.neutral"));
        setSubmitting(false);
        return;
      }

      if (
        Array.isArray(selectedItems.vision) &&
        selectedItems.vision.includes(
          "Headaches caused by close work: computers, reading, gaming"
        )
      ) {
        if (headacheParts.length > 0) {
          addSymptomDetails({
            patientId: patient.patientId,
            data: {
              symptom: "Headaches caused by close work: computers, reading, gaming",
              detail: headacheParts,
            },
          });
        } else {
          setError(t("selectlatersymptoms.error.headache_location"));
          setSubmitting(false);
          return;
        }
      }

      postRegisterFactors({ patientId: patient.patientId, data: processedData });

      postNewResultingFactors({
        patientId: patient.patientId,
        data: processedData,
      }).then(() => {
        location?.state?.fromPatientDashboard
          ? history.push("/selectTopSymptoms", {
              forwardData: { selectedItems, processedData, dateOfTBI },
              fromPatientControl: location.state?.fromPatientControl,
              fromPatientDashboard: true,
            })
          : history.push("/selectTopSymptoms", {
              forwardData: { selectedItems, processedData, dateOfTBI },
              fromPatientControl: location.state?.fromPatientControl,
            });
      });
    } catch (err) {
      console.log("some error in SelectLaterSymptoms handleSubmit");
      console.log(err);
      history.push("/oops");
    }
  };

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <>
          <div className="flex flex-col items-center">
            <div className="w-2/3">
              <OnboardingStepHeader text={getMessage("mainHeader")} />
              <Sallie text={getMessage("sallieText")} />
              <div className="flex flex-col md:flex-row gap-8 my-8">
                <div className="flex-1 h-auto md:h-64">
                  <img src={brainA} className="w-full h-full md:object-fill object-contain" />
                </div>
                <div className="flex-1 h-auto md:h-64">
                  <img src={brainB} className="w-full h-full md:object-fill object-contain" />
                </div>
              </div>
            </div>
          </div>

          <h5 className="text-center mb-8">
            {t("selectlatersymptoms.category_header")}
          </h5>

          <div className="flex flex-col items-center w-full md:w-2/3 mx-auto gap-8">
            <div className="flex flex-col w-full gap-2">
              <CategorySelector
                categoryType="Symptoms"
                categorizedItemList={returnedData}
                setCategorizedItemList={setReturnedData}
                colors={colors}
                currentCategoryIndex={currentCategoryIndex}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                setError={setError}
                headacheParts={headacheParts}
                setHeadacheParts={setHeadacheParts}
              />
              {error && <p className="text-red-500">{error}</p>}
              {errorMessage && (
                <Form.Control.Feedback style={{ display: "block" }} type="invalid">
                  {isSMI
                    ? t("selectlatersymptoms.error.min_symptoms_smi")
                    : t("selectlatersymptoms.error.min_symptoms")}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="flex justify-between w-full">
              <SubmitButton
                type="button"
                onClick={handlePrev}
                disabled={currentCategoryIndex === 0}
                greyout={currentCategoryIndex === 0}
              >
                {t("selectlatersymptoms.previous_category")}
              </SubmitButton>
              <SubmitButton
                type="button"
                onClick={handleNextOrSubmit}
                disabled={submitting}
              >
                {currentCategoryIndex < categories.length - 1
                  ? t("selectlatersymptoms.next_category")
                  : `${t("onboarding.saveContinue")} >`}
              </SubmitButton>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SelectLaterSymptoms;